import * as React from "react";
import PermissionBrowser from "./PermissionBrowser";
import { Assignment, Capability, Snapshot } from "./OrganizationPermissionStore";

interface Props { definitions: Capability[]; snapshot: Snapshot; onChange: (permission: string, assignment: Assignment, revision: string) => Promise<void>; }
export default function PermissionAssignmentBrowser({ definitions, snapshot, onChange }: Props) {
    const descriptions = React.useMemo(() => definitions.map(p => ({
        name: p.permissionId,
        displayName: p.displayName,
        description: p.description || `Default access: ${p.defaultDecision}.`,
        category: p.domain === "identity" ? undefined : p.displayName.split(" / ")[0],
    })), [definitions]);
    const states = Object.fromEntries(snapshot.permissions.map(p => [p.permissionId, p]));
    const change = async (permission: string, assignment: Assignment) => {
        const state = states[permission];
        if (!state?.editable || !state.revision) throw new Error("Permission is not ready for editing.");
        await onChange(permission, assignment, state.revision);
    };
    return <div className="space-y-4">
        <p className="text-sm text-gray-500">Removing a direct assignment restores inheritance from roles and defaults. Denials take precedence over grants.</p>
        {!!snapshot.unresolvedItems.length && <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">{snapshot.unresolvedItems.length} additional items need mapping review. Their values are preserved.</p>}
        <PermissionBrowser
            definitions={descriptions}
            assignedPermissions={snapshot.permissions.filter(p => p.assignment === "allow").map(p => p.permissionId)}
            deniedPermissions={snapshot.permissions.filter(p => p.assignment === "deny").map(p => p.permissionId)}
            assignmentStates={states}
            canModify={permission => !!states[permission]?.editable}
            disabledReason="You have view-only access to this permission."
            onChange={(permission, assigned) => change(permission, assigned ? "allow" : "inherit")}
            onAssignmentChange={change}
        />
        <p className="text-xs text-gray-500">Access verified {new Date(snapshot.readAt).toLocaleString()}.</p>
    </div>;
}
